// =============================================================
//  Theme controller — Light / Hybrid / Dark / System
// -------------------------------------------------------------
//  - Sets the EFFECTIVE theme on <html> instantly, no reload:
//      data-theme         light | hybrid | dark   (what the page shows)
//      data-theme-pref    light | hybrid | dark | system (what the user chose)
//      data-bs-theme      light | dark            (Bootstrap internals)
//      classes            theme-light / theme-hybrid / theme-system
//  - Persists the choice to the server (PATCH /theme/:theme)
//  - "system" follows prefers-color-scheme: dark → dark, light → hybrid
//    (the app's default light look). The resolved scheme is mirrored in
//    the aim_scheme cookie so the server renders the right classes on the
//    next load (no flash); a live OS switch re-applies immediately.
//  - Charts (lib/appcharts.js) follow <html data-theme> via an observer.
//  - Turbo keeps <html> across visits (it only copies lang and dir), so the
//    server also puts the choice on <body data-theme-pref>, and every Turbo
//    render takes it over before the new page shows (turbo:before-render).
// =============================================================

const SYSTEM_QUERY = '(prefers-color-scheme: dark)'
const PREFS = ['light', 'hybrid', 'dark', 'system']

// Opslagen die nog lopen (PATCH /theme/:theme). Zolang er een loopt, wint de
// keuze van net boven wat een Turbo-render meebrengt: de server kan die pagina
// al gerenderd hebben voordat de keuze binnen was.
let saving = 0

function pref () {
  const p = document.documentElement.dataset.themePref
  return PREFS.includes(p) ? p : 'hybrid'
}

function osDark () {
  return window.matchMedia && window.matchMedia(SYSTEM_QUERY).matches
}

function resolve (theme) {
  if (theme === 'system') return osDark() ? 'dark' : 'hybrid'
  return theme
}

function setCookie (name, value) {
  document.cookie = name + '=' + value + '; path=/; max-age=' + (60 * 60 * 24 * 365) + '; SameSite=Lax'
}

// ---- Switch UI (4-way) --------------------------------------
function syncSwitch (theme) {
  document.querySelectorAll('.theme-switch3').forEach(function (el) {
    el.dataset.theme = theme
    el.querySelectorAll('[data-set-theme]').forEach(function (b) {
      const on = b.dataset.setTheme === theme
      b.classList.toggle('is-active', on)
      b.setAttribute('aria-pressed', on ? 'true' : 'false')
    })
  })
}

// Browser-chrome colour (status bar in an installed PWA) follows the canvas
// token of the effective theme. Mirrors ApplicationHelper#theme_color_hex.
function syncThemeColorMeta (effective) {
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', effective === 'dark' ? '#17181c' : '#eef0f3')
}

function applyTheme (theme, animate) {
  const html = document.documentElement
  const effective = resolve(theme)
  if (animate) {
    html.classList.add('theme-anim')
    window.setTimeout(function () { html.classList.remove('theme-anim') }, 450)
  }
  html.dataset.themePref = theme
  html.dataset.theme = effective
  html.dataset.bsTheme = effective === 'dark' ? 'dark' : 'light'
  html.classList.toggle('theme-light', effective === 'light' || effective === 'hybrid')
  html.classList.toggle('theme-hybrid', effective === 'hybrid')
  html.classList.toggle('theme-system', theme === 'system')
  setCookie('aim_scheme', osDark() ? 'dark' : 'light')
  syncThemeColorMeta(effective)
  syncSwitch(theme)
}

function persistTheme (theme) {
  const token = document.querySelector('meta[name="csrf-token"]')
  saving += 1
  return fetch('/theme/' + theme, {
    method: 'PATCH',
    headers: { 'X-CSRF-Token': token ? token.getAttribute('content') : '', Accept: 'application/json' },
    credentials: 'same-origin'
  }).catch(function () { /* visual already applied; server re-syncs next load */ })
    .finally(function () { saving -= 1 })
}

// ---- Wiring -------------------------------------------------
document.addEventListener('click', function (e) {
  const btn = e.target.closest ? e.target.closest('[data-set-theme]') : null
  if (!btn) return
  e.preventDefault()
  const next = btn.dataset.setTheme
  if (next && next !== pref()) { applyTheme(next, true); persistTheme(next) }
})

// Live OS switch while "system" is selected.
if (window.matchMedia) {
  const mq = window.matchMedia(SYSTEM_QUERY)
  const onChange = function () { if (pref() === 'system') applyTheme('system', true) }
  if (mq.addEventListener) mq.addEventListener('change', onChange)
  else if (mq.addListener) mq.addListener(onChange)
}

// Turbo vervangt bij een visit <body> en voegt <head> samen, maar neemt van
// <html> alleen lang en dir over. Het thema bleef daardoor dat van de laatste
// volledige paginalading: na het inloggen (een Turbo-formulier) het donkere
// thema van de loginpagina, tot een volledige lading — zoals de profiellink —
// ineens de echte instelling liet zien. De server zet de keuze daarom ook op
// <body>, en die nemen we hier over vóór de nieuwe body in beeld komt.
document.addEventListener('turbo:before-render', function (e) {
  const body = e.detail && e.detail.newBody
  const next = body && body.dataset ? body.dataset.themePref : null
  if (!PREFS.includes(next) || saving > 0 || next === pref()) return
  applyTheme(next, false)
})

function boot () {
  // Server-rendered state is already correct; re-apply (no animation) so the
  // switch, the cookie and Bootstrap's attribute are in sync — and so a
  // "system" user whose OS changed since the last visit gets the right look.
  applyTheme(pref(), false)
}

document.addEventListener('turbo:load', boot)
boot()
